import { Box, Flex, Text } from '@chakra-ui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import type { ContractAbiItemInput } from '../types';

import { useI18n } from 'lib/i18n/context';

import { ADDRESS_CONTRACT_KEYS } from '../../../i18n';
import { applyBatchAddressesToForm, getInitialAddressArrayIndices, getInitialAddressArrayRowCount } from './addressArrayFieldUtils';
import ContractMethodAddressArrayBatchPaste from './ContractMethodAddressArrayBatchPaste';
import ContractMethodAddressArrayIconButton from './ContractMethodAddressArrayIconButton';
import ContractMethodAddressInputField from './ContractMethodAddressInputField';
import ContractMethodFieldLabel from './ContractMethodFieldLabel';
import ContractMethodFieldRow from './ContractMethodFieldRow';
import {
  CONTRACT_METHOD_ACTION_GAP,
  CONTRACT_METHOD_FIELD_INPUT_HEIGHT,
  CONTRACT_METHOD_ROW_NUMBER_WIDTH,
  useContractMethodFieldStyles,
} from './contractMethodFieldStyles';
import { transformDataForArrayItem } from './utils';

interface Props {
  data: ContractAbiItemInput;
  basePath: string;
  level: number;
  isDisabled: boolean;
  hasFixedSize: boolean;
  maxSize: number;
}

const ContractMethodFieldInputAddressArray = ({
  data,
  basePath,
  level,
  isDisabled,
  hasFixedSize,
  maxSize,
}: Props) => {
  const { t } = useI18n();
  const { setValue, getValues, unregister } = useFormContext();
  const minRowCount = React.useMemo(() => getInitialAddressArrayRowCount(hasFixedSize, maxSize), [ hasFixedSize, maxSize ]);
  const [ registeredIndices, setRegisteredIndices ] = React.useState(() => getInitialAddressArrayIndices(hasFixedSize, maxSize));
  const [ isBatchOpen, setIsBatchOpen ] = React.useState(false);
  const [ batchPasteKey, setBatchPasteKey ] = React.useState(0);
  const styles = useContractMethodFieldStyles();

  const handleAddButtonClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (hasFixedSize && registeredIndices.length >= maxSize) {
      return;
    }

    setRegisteredIndices((prev) => [ ...prev, prev[prev.length - 1] + 1 ]);
  }, [ hasFixedSize, maxSize, registeredIndices.length ]);

  const handleRemoveButtonClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const { index } = event.currentTarget.dataset;
    const itemIndex = Number(index);

    if (Number.isNaN(itemIndex)) {
      return;
    }

    setRegisteredIndices((prev) => {
      const next = prev.filter((registeredIndex) => registeredIndex !== itemIndex);
      return next.length >= minRowCount ? next : prev;
    });
  }, [ minRowCount ]);

  const handleBatchToggle = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (isBatchOpen) {
      setIsBatchOpen(false);
      setBatchPasteKey((prev) => prev + 1);
      return;
    }

    setIsBatchOpen(true);
  }, [ isBatchOpen ]);

  const handleBatchClose = React.useCallback(() => {
    setIsBatchOpen(false);
    setBatchPasteKey((prev) => prev + 1);
  }, []);

  const handleBatchAdd = React.useCallback((addresses: Array<string>) => {
    const nextIndices = applyBatchAddressesToForm({
      basePath,
      registeredIndices,
      addresses,
      hasFixedSize,
      maxSize,
      getValues,
      setValue,
      unregister,
    });

    setRegisteredIndices(nextIndices);
    setIsBatchOpen(false);
    setBatchPasteKey((prev) => prev + 1);
  }, [ basePath, getValues, hasFixedSize, maxSize, registeredIndices, setValue, unregister ]);

  const canAddMore = !hasFixedSize || registeredIndices.length < maxSize;

  return (
    <ContractMethodFieldRow
      label={ (
        <ContractMethodFieldLabel
          data={ data }
          isOptional={ registeredIndices.length === 1 && !hasFixedSize }
          level={ level }
        />
      ) }
    >
      { registeredIndices.map((registeredIndex, index) => {
        const itemData = transformDataForArrayItem(data, index);
        const isLastRow = index === registeredIndices.length - 1;
        const showRemoveButton = !isLastRow && registeredIndices.length > minRowCount;

        return (
          <Flex
            key={ registeredIndex }
            alignItems="flex-start"
            w="100%"
          >
            <Flex
              w={ CONTRACT_METHOD_ROW_NUMBER_WIDTH }
              flexShrink={ 0 }
              alignItems="center"
              justifyContent="center"
              h={ CONTRACT_METHOD_FIELD_INPUT_HEIGHT }
            >
              <Text fontSize="12px" fontWeight={ 500 } color={ styles.labelColor } lineHeight="22px">
                { index + 1 }
              </Text>
            </Flex>
            <Flex alignItems="flex-start" columnGap={ CONTRACT_METHOD_ACTION_GAP } flex={ 1 } minW={ 0 }>
              <Box flex={ 1 } minW={ 0 }>
                <ContractMethodAddressInputField
                  data={ itemData }
                  path={ `${ basePath }:${ index }` }
                  isDisabled={ isDisabled }
                  isOptional={ registeredIndices.length === 1 && !hasFixedSize }
                />
              </Box>
              <Flex
                alignItems="center"
                columnGap={ CONTRACT_METHOD_ACTION_GAP }
                flexShrink={ 0 }
                h={ CONTRACT_METHOD_FIELD_INPUT_HEIGHT }
              >
                { showRemoveButton && (
                  <ContractMethodAddressArrayIconButton
                    label={ t(ADDRESS_CONTRACT_KEYS.actions.remove) }
                    iconName="address-array-minus"
                    onClick={ handleRemoveButtonClick }
                    isDisabled={ isDisabled }
                    styles={ styles }
                    dataIndex={ registeredIndex }
                  />
                ) }
                { isLastRow && canAddMore && (
                  <ContractMethodAddressArrayIconButton
                    label={ t(ADDRESS_CONTRACT_KEYS.actions.add) }
                    iconName="address-array-plus"
                    onClick={ handleAddButtonClick }
                    isDisabled={ isDisabled }
                    styles={ styles }
                  />
                ) }
                { isLastRow && (
                  <ContractMethodAddressArrayIconButton
                    label={ t(ADDRESS_CONTRACT_KEYS.batchPaste.open) }
                    iconName="address-batch-paste"
                    onClick={ handleBatchToggle }
                    isDisabled={ isDisabled }
                    styles={ styles }
                    bordered
                    active={ isBatchOpen }
                  />
                ) }
              </Flex>
            </Flex>
          </Flex>
        );
      }) }
      { isBatchOpen && (
        <Box pl={ CONTRACT_METHOD_ROW_NUMBER_WIDTH } w="100%">
          <ContractMethodAddressArrayBatchPaste
            key={ batchPasteKey }
            onAdd={ handleBatchAdd }
            onClose={ handleBatchClose }
          />
        </Box>
      ) }
    </ContractMethodFieldRow>
  );
};

export default React.memo(ContractMethodFieldInputAddressArray);
